import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
} from "@mui/material";
import {
  SportsSoccer as SoccerIcon,
  ArrowForward as ArrowIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { get } from "../utils/networkUtils";

// Helper function to detect World Cup tournaments
const isWorldCupTournament = (tournament) => {
  return (
    tournament.competition_name?.toLowerCase().includes("world cup") ||
    tournament.competition_name?.toLowerCase().includes("fifa world")
  );
};

const TournamentListPage = () => {
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all tournaments
  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get("/admin/tournament-calendar?page=1&limit=100");

      if (response?.data) {
        // Backend returns { success: true, data: { tournamentCalendars: [...], pagination: {...} } }
        const tournamentList =
          response.data.data?.tournamentCalendars ||
          response.data.tournamentCalendars ||
          response.data ||
          [];
        setTournaments(tournamentList);
      } else {
        setTournaments([]);
      }
    } catch (err) {
      console.error("Failed to fetch tournaments:", err);
      setError("Failed to load tournaments. Please try again.");
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, []);

  // Filter tournaments
  const filteredTournaments = tournaments.filter((t) => {
    const query = searchQuery.toLowerCase();
    return (
      (t.competition_name?.toLowerCase() || "").includes(query) ||
      (t.season_label?.toLowerCase() || "").includes(query) ||
      (t.short_code?.toLowerCase() || "").includes(query)
    );
  });

  // Separate World Cup from other tournaments
  const worldCupTournaments = filteredTournaments.filter(isWorldCupTournament);

  const otherTournaments = filteredTournaments.filter(
    (t) => !isWorldCupTournament(t)
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "default";
      case "paused":
        return "warning";
      case "completed":
        return "info";
      case "archived":
        return "error";
      default:
        return "default";
    }
  };

  const handleTournamentClick = (tournament) => {
    // Navigate to tournament detail page for all tournaments
    // The detail page will handle World Cup vs other tournaments appropriately
    navigate(`/dashboard/tournaments/${tournament.id}`);
  };

  const TournamentCard = ({ tournament }) => {
    const worldCup = isWorldCupTournament(tournament);

    return (
      <Card
        sx={{
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }}
        onClick={() => handleTournamentClick(tournament)}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SoccerIcon color="primary" />
              <Typography variant="h6" component="div">
                {tournament.competition_name}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Chip
                label={worldCup ? "Matchdays" : "Gameweeks"}
                color={worldCup ? "primary" : "secondary"}
                size="small"
                variant="outlined"
              />
              <Chip
                label={tournament.status || "draft"}
                color={getStatusColor(tournament.status)}
                size="small"
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Season: {tournament.season_label || "N/A"}
          </Typography>

          {tournament.short_code && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Code: {tournament.short_code}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <ArrowIcon color="action" fontSize="small" />
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tournaments | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          Tournaments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage tournaments and their configurations
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search tournaments by name, season, or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 4 }}
        />

        {/* World Cup Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h5">World Cup</Typography>
            <Tooltip title="Matchday-based tournament management">
              <Chip
                label="Matchdays"
                color="primary"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {worldCupTournaments.length === 0 ? (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("/dashboard/ecff/opta-import")}
                >
                  Go to OPTA Import
                </Button>
              }
            >
              No World Cup tournament found. Please import the World Cup from
              OPTA first.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {worldCupTournaments.map((tournament) => (
                <Grid item xs={12} sm={6} md={4} key={tournament.id}>
                  <TournamentCard tournament={tournament} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Other Tournaments Section */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h5">Other Tournaments</Typography>
            <Tooltip title="Gameweek-based tournament management (FPL, UCL, etc.)">
              <Chip
                label="Gameweeks"
                color="secondary"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {otherTournaments.length === 0 ? (
            <Alert severity="info">
              No other tournaments found. Tournaments are imported from OPTA.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {otherTournaments.map((tournament) => (
                <Grid item xs={12} sm={6} md={4} key={tournament.id}>
                  <TournamentCard tournament={tournament} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </>
  );
};

export default TournamentListPage;
